// src/Components/LoginModal.jsx
import React, { useState } from "react";
import { Mail } from "lucide-react";
import authService from "../Services/authService";
import { authErrorMessage } from "../Services/authErrors";
import BottomSheet from "./BottomSheet";
import {
  TextField,
  PasswordField,
  PrimaryButton,
  FormError,
  LinkButton,
  OrDivider,
  GoogleButton,
} from "./auth/AuthFields";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginModal = ({ isOpen, onClose, onSuccess, onSwitchToSignUp }) => {
  const [view, setView] = useState("main"); // main | link | reset | sent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [linkEmail, setLinkEmail] = useState("");
  const [linkPassword, setLinkPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");

  const busy = loading || googleLoading;

  const resetForm = () => {
    setView("main");
    setEmail("");
    setPassword("");
    setFieldErrors({});
    setError("");
    setLinkEmail("");
    setLinkPassword("");
    setResetEmail("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const finish = (result) => {
    if (onSuccess) onSuccess(result);
    handleClose();
  };

  const goTo = (next) => {
    setView(next);
    setError("");
    setFieldErrors({});
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError("");
    setFieldErrors({});

    try {
      const result = await authService.signInWithGoogle();
      if (result.success) {
        finish(result);
      } else if (result.error === "email_exists" && result.canLink) {
        setLinkEmail(result.googleEmail || "");
        goTo("link");
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error("Google login error:", err);
      setError(authErrorMessage(err, "We couldn't sign you in with Google"));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleLinkAccounts = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!EMAIL_RE.test(linkEmail.trim())) errors.email = "Enter the email for your existing account.";
    if (!linkPassword) errors.password = "Enter your password.";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    setError("");

    try {
      const result = await authService.linkGoogleToExistingEmailAccount(null, linkEmail.trim(), linkPassword);
      if (result.success) finish(result);
      else setError(result.message);
    } catch (err) {
      console.error("Link accounts error:", err);
      setError(authErrorMessage(err, "We couldn't link your accounts"));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!email.trim()) errors.email = "Enter your email.";
    else if (!EMAIL_RE.test(email.trim())) errors.email = "That email doesn't look right.";
    if (!password) errors.password = "Enter your password.";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    setError("");

    try {
      const result = await authService.signInWithEmail(email.trim(), password);
      if (result.success) finish(result);
      else setError(result.message);
    } catch (err) {
      console.error("Email login error:", err);
      setError(authErrorMessage(err, "We couldn't sign you in"));
    } finally {
      setLoading(false);
    }
  };

  const openReset = () => {
    setResetEmail(email);
    goTo("reset");
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(resetEmail.trim())) {
      setFieldErrors({ email: "Enter the email you signed up with." });
      return;
    }
    setLoading(true);
    setError("");
    setFieldErrors({});
    try {
      const result = await authService.sendPasswordReset(resetEmail);
      if (result.success) setView("sent");
      else setError(result.message);
    } catch (err) {
      setError(authErrorMessage(err, "We couldn't send the reset email"));
    } finally {
      setLoading(false);
    }
  };

  const switchToSignUp = () => {
    resetForm();
    onSwitchToSignUp();
  };

  const titles = {
    main: ["Sign in", "Welcome back"],
    link: ["Link your accounts", "This email already has an account"],
    reset: ["Reset your password", "We'll email you a reset link"],
    sent: ["Check your email", null],
  };
  const [title, subtitle] = titles[view];

  return (
    <BottomSheet isOpen={isOpen} onClose={busy ? () => {} : handleClose} title={title} subtitle={subtitle}>
      {view === "main" && (
        <>
          <GoogleButton onClick={handleGoogleLogin} loading={googleLoading} disabled={loading} />
          <OrDivider />
          <form onSubmit={handleEmailLogin} noValidate className="space-y-4">
            <TextField
              label="Email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              error={fieldErrors.email}
              placeholder="you@example.com"
            />
            <PasswordField
              label="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              error={fieldErrors.password}
              placeholder="Your password"
              labelAction={
                <LinkButton onClick={openReset} disabled={busy}>
                  Forgot password?
                </LinkButton>
              }
            />
            <FormError>{error}</FormError>
            <PrimaryButton loading={loading} loadingLabel="Signing in…" disabled={busy}>
              Sign in
            </PrimaryButton>
          </form>
          {onSwitchToSignUp && (
            <p className="mt-4 text-center text-sm text-brand-textSecondary">
              New to NameIt!?{" "}
              <button
                type="button"
                onClick={switchToSignUp}
                disabled={busy}
                className="min-h-[44px] font-semibold text-brand-accent hover:underline"
              >
                Create an account
              </button>
            </p>
          )}
        </>
      )}

      {view === "link" && (
        <form onSubmit={handleLinkAccounts} noValidate className="space-y-4">
          <p className="text-sm text-brand-textSecondary">
            Enter your password to link your Google account to your existing NameIt! account.
          </p>
          <TextField
            label="Email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={linkEmail}
            onChange={(e) => setLinkEmail(e.target.value)}
            disabled={loading}
            error={fieldErrors.email}
          />
          <PasswordField
            label="Password"
            autoComplete="current-password"
            value={linkPassword}
            onChange={(e) => setLinkPassword(e.target.value)}
            disabled={loading}
            error={fieldErrors.password}
          />
          <FormError>{error}</FormError>
          <PrimaryButton loading={loading} loadingLabel="Linking…">
            Link accounts
          </PrimaryButton>
          <button
            type="button"
            onClick={() => goTo("main")}
            className="w-full min-h-[44px] text-sm font-semibold text-brand-textSecondary hover:text-brand-text"
          >
            Cancel
          </button>
        </form>
      )}

      {view === "reset" && (
        <form onSubmit={handleSendReset} noValidate className="space-y-4">
          <TextField
            label="Email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            disabled={loading}
            error={fieldErrors.email}
            placeholder="you@example.com"
          />
          <FormError>{error}</FormError>
          <PrimaryButton loading={loading} loadingLabel="Sending…">
            Send reset link
          </PrimaryButton>
          <button
            type="button"
            onClick={() => goTo("main")}
            className="w-full min-h-[44px] text-sm font-semibold text-brand-textSecondary hover:text-brand-text"
          >
            Back to sign in
          </button>
        </form>
      )}

      {view === "sent" && (
        <div className="space-y-4 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-brand-accentMuted flex items-center justify-center">
            <Mail className="w-6 h-6 text-brand-accent" aria-hidden="true" />
          </div>
          <p className="text-sm text-brand-textSecondary">
            If there's an account for <span className="font-semibold text-brand-text">{resetEmail.trim()}</span>, we've
            sent a link to reset your password. It can take a minute, and it may land in spam.
          </p>
          <button
            type="button"
            onClick={() => {
              setEmail(resetEmail.trim());
              setPassword("");
              goTo("main");
            }}
            className="w-full h-12 rounded-xl bg-brand-accent text-white font-semibold hover:bg-red-700 transition-colors"
          >
            Back to sign in
          </button>
        </div>
      )}
    </BottomSheet>
  );
};

export default LoginModal;
